import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import './StaffRolesPage.scss'
import merchantApi from '../../services/merchantApi'
import { Icon } from '@iconify/react/dist/iconify.js'
import Header from '../../components/Header/Header'
import AppTable from '../../components/AppTable/AppTable'
import { Divider, Grid, Avatar } from '@mui/material'
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown'
import StatusFilterDropdown from '../../components/StatusFilterDropdown/StatusFilterDropdown'
import IconSelect from '../../components/IconSelect/IconSelect';
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal';
import PhoneInputField from '../../components/PhoneInputField/PhoneInputField'
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import moment from 'moment';

const moduleCode = 'staff'

// ─── Data ─────────────────────────────────────────────────────────────────────

const ROLES_ICON = {
  owner: 'ph:lock-simple-fill',
  manager: 'ph:tag-fill',
  cashier: 'ph:money-fill',
  "kitchen staff": 'ph:cooking-pot-fill',
  waiter: 'ph:fork-knife-fill',
}

const ROLES = [
  {
    id: 'owner',
    name: 'Owner',
    description: 'Full access to all features and settings',
    color: 'owner',
    icon: 'ph:lock-simple-fill',
  },
  {
    id: 'manager',
    name: 'Manager',
    description: 'Manages day-to-day operations — orders, catalog, staff',
    color: 'manager',
    icon: 'ph:tag-fill',
  },
  {
    id: 'cashier',
    name: 'Cashier',
    description: 'Handles payment processing and order confirmation',
    color: 'cashier',
    icon: 'ph:money-fill',
  },
  {
    id: 'kitchen_staff',
    name: 'Kitchen staff',
    description: 'Views and updates order status — kitchen-only access',
    color: 'kitchen',
    icon: 'ph:cooking-pot-fill',
  },
  {
    id: 'waiter',
    name: 'Waiter',
    description: 'Takes dine-in orders and manages table service',
    color: 'waiter',
    icon: 'ph:fork-knife-fill',
  },
]

const ICON_OPTIONS = [
  'ph:lock-simple-fill',
  'ph:tag-fill',
  'ph:money-fill',
  'ph:cooking-pot-fill',
  'ph:fork-knife-fill',
  'ph:users-fill',
]

function normalizePhoneNumber(value) {
  return String(value || '').replace(/^\+?60/, '').replace(/\D/g, '')
}

function getPhoneParts(value) {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\+\d{2,4})/)
  return {
    countryCode: match?.[1] || '60',
    number: raw.replace(/^(\+\d{2,4})/, '').replace(/\D/g, ''),
  }
}

function normalizeStaffPhoneFields(staff = {}) {
  const parsedPhone = parsePhoneNumberFromString(staff.phone ? `+${staff.phone}` : '')
  const fallbackPhone = getPhoneParts(staff.phone)
  return {
    ...staff,
    countryCode: parsedPhone?.countryCallingCode ? parsedPhone.countryCallingCode : '60',
    phone: parsedPhone?.nationalNumber || fallbackPhone.number,
  }
}

// ─── Role badge ───────────────────────────────────────────────────────────────

function RoleBadge({ roleId }) {
  const role = ROLES.find((r) => r.id === roleId)
  if (!role) return null
  return (
    <span className={`sr-role-badge sr-role-badge--${role.color}`}>
      {role.name}
    </span>
  )
}

// ─── Invite Staff Modal ───────────────────────────────────────────────────────

function InviteModal({ outlets = [], roles = [], onClose, onInvited }) {
  const fallbackOutlets = outlets.length ? outlets : [{ id: 1, name: 'KLCC Branch' }]
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCountryCode, setPhoneCountryCode] = useState('60')
  const [email, setEmail] = useState('')
  const [outletId, setOutletId] = useState(fallbackOutlets[0]?.id || 1)
  const [roleId, setRoleId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!outletId && fallbackOutlets[0]?.id) {
      setOutletId(fallbackOutlets[0].id)
    }
  }, [fallbackOutlets, outletId])

  
  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim() || !roleId || submitting) return

    setSubmitting(true)
    setError('')
    try {
      const payload = {
        name: name.trim(),
        phone_country_code: phoneCountryCode,
        phone_number: normalizePhoneNumber(phone),
        email: email.trim() || null,
        outlet_id: Number(outletId),
        role_id: Number(roleId),
      }

      const response = await merchantApi.staff.invite(payload)
      onInvited?.(response, payload)
      onClose()
    } catch (err) {
      setError(err?.message || 'Unable to send staff invitation.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="sr-modal-overlay" onClick={onClose}>
      <div className="sr-modal" onClick={(e) => e.stopPropagation()}>

        <div className="sr-modal__header">
          <h3>Invite staff member</h3>
          <button className="sr-modal__close" onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        <div className="sr-modal__body">
          {error && <div className="sr-error">{error}</div>}

          <p className="sr-modal__section-label">BASIC INFO</p>

          <div className="sr-modal__field">
            <label>Name</label>
            <input className="sr-input" type="text" placeholder="Amber Ng Zi Qing" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="sr-modal__field">
            <label>Phone number</label>
            <PhoneInputField
              value={phone}
              onChange={setPhone}
              countryCode={phoneCountryCode}
              onCountryCodeChange={setPhoneCountryCode}
              placeholder="123889021"
            />
          </div>

          <div className="sr-modal__field">
            <label>Email address <span className="sr-modal__optional">(optional)</span></label>
            <input className="sr-input" type="email" placeholder="amberng@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div className="sr-modal__field">
            <label>Outlet</label>
            <div className="sr-select-wrap">
              <select className="sr-select" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
                {fallbackOutlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <Icon icon="ph:chevron-down" width={16} className="sr-select-wrap__icon" />
            </div>
          </div>

          <p className="sr-modal__section-label">ROLE</p>

          <div className="sr-role-list">
            {roles.filter((r) => (r.id !== 'owner' && r.display_name !== 'Owner')).map((role) => {
              return (
                <button
                  key={role.id}
                  type="button"
                  className={`sr-role-option ${roleId === role.id ? 'sr-role-option--active' : ''}`}
                  onClick={() => setRoleId(role.id)}
                >
                  <span className={`sr-role-option__icon`}>
                    <Icon icon={role.display_name.toLowerCase() in ROLES_ICON ? ROLES_ICON[role.display_name.toLowerCase()] : role.icon} size={18} />
                  </span>
                  <span className="sr-role-option__body">
                    <strong>{role.display_name}</strong>
                    <span>{role.description}</span>
                  </span>
                  {roleId === role.id && (
                    <span className="sr-role-option__check">✓</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        <div className="sr-modal__footer">
          <button className="sr-btn sr-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="sr-btn sr-btn--primary" onClick={handleSubmit} disabled={!name || !phone || !roleId || submitting}>
            {submitting ? 'Sending...' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Deactivate Modal ───────────────────────────────────────────────────────

function DeactivateModal({ selectedStaff, onClose, onDeactivated }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const handleSubmit = async () => {
    if (!selectedStaff || submitting) return

    setSubmitting(true)
    setError('')

    try {
      const payload = {
        status: 0,
      }

      const response = await merchantApi.staff.updateStatus(selectedStaff.id, payload)
      onDeactivated(response, selectedStaff)
      onClose()
    } catch (err) {
      setError(err?.message || 'Unable to deactivate staff account.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="sr-modal-overlay sr-modal-overlay--above-panel" onClick={onClose}>
      <div className="sr-modal" onClick={(e) => e.stopPropagation()}>

        <div className="sr-modal__header">
          <h3>Deactivate account</h3>
          <button className="sr-modal__close" onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        <div className="sr-modal__body">
          {error && <div className="sr-error">{error}</div>}

          <div >Deactivating <strong>{selectedStaff?.name}</strong> will immediately:</div>

          <ul className="cdp-modal__consequences">
            <li>Block all login attempts for this account</li>
            <li>End all active sessions across all devices</li>
            <li>Preserve audit logs and activity history</li>
          </ul>

          <div className='notification-container'>
            <Icon icon={'ph:info'} className='notification-container--icon-style' height={20} />
            <div>The account can be reactivated at any time from this screen. Data is never deleted.</div>
          </div>
        </div>

        <div className="sr-modal__footer">
          <button className="sr-btn sr-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="sr-btn sr-btn--deactivate-solid" onClick={handleSubmit} disabled={!selectedStaff || submitting}>
            {submitting ? 'Deactivating...' : 'Deactivate account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Reactivate Modal ───────────────────────────────────────────────────────

function ReactivateModal({ selectedStaff, onClose, onReactivated }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!selectedStaff || submitting) return

    setSubmitting(true)
    setError('')

    try {
      const response = await merchantApi.staff.updateStatus(selectedStaff.id, { status: 1 })
      onReactivated(response, selectedStaff)
      onClose()
    } catch (err) {
      setError(err?.message || 'Unable to reactivate staff account.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="sr-modal-overlay sr-modal-overlay--above-panel" onClick={onClose}>
      <div className="sr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sr-modal__header">
          <h3>Reactivate account</h3>
          <button className="sr-modal__close" onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        <div className="sr-modal__body">
          {error && <div className="sr-error">{error}</div>}

          <div>Reactivate the account for <strong>{selectedStaff?.name}</strong>?</div>

          <div className="notification-container">
            <Icon icon="ph:info" className="notification-container--icon-style" height={20} />
            <div>This staff member will be able to log in again and access their assigned outlets.</div>
          </div>
        </div>

        <div className="sr-modal__footer">
          <button className="sr-btn sr-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="sr-btn sr-btn--save" onClick={handleSubmit} disabled={!selectedStaff || submitting}>
            {submitting ? 'Reactivating...' : 'Reactivate account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Create new role Modal ───────────────────────────────────────────────────────

function CreateRoleModal({ onClose, onRoleAdded }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [roleObj, setRoleObj] = useState({ name: '', description: '', icon: '' })

  const handleSubmit = async () => {

    setSubmitting(true)
    setError('')

    try {
      const payload = {
        display_name: roleObj.name,
        description: roleObj.description,
        icon: roleObj.icon,
      }

      const response = await merchantApi.roles.create(payload)
      onRoleAdded(response, payload)
      onClose()
    } catch (err) {
      setError(err?.message || 'Unable to create role.')
    } finally {
      setSubmitting(false)
    }
  }

  return createPortal(
    <div className="sr-modal-overlay" onClick={onClose}>
      <div className="sr-modal" onClick={(e) => e.stopPropagation()}>

        <div className="sr-modal__header">
          <h3>Add new role</h3>
          <button className="sr-modal__close" onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        <div className="sr-modal__body">
          {error && <div className="sr-error">{error}</div>}

          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <div className="sr-modal__field">
                <label>Role</label>
                <input className="sr-input" type="text" placeholder="Role name" value={roleObj.name} onChange={(e) => setRoleObj({ ...roleObj, name: e.target.value })} />
              </div>
            </Grid>
            <Grid item sm={12} xs={12}>
              <IconSelect
                label="Icon"
                value={roleObj.icon}
                options={ICON_OPTIONS}
                onChange={(e) => setRoleObj({ ...roleObj, icon: e.target.value })}
                placeholder="Select icon"
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <div className="sr-modal__field">
                <label>Description</label>
                <input className="sr-input" type="text" placeholder="Role description" value={roleObj.description} onChange={(e) => setRoleObj({ ...roleObj, description: e.target.value })} />
              </div>
            </Grid>
          </Grid>
        </div>

        <div className="sr-modal__footer">
          <button className="sr-btn sr-btn--outline" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="sr-btn sr-btn--standard" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Creating...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Staff Details Panel ──────────────────────────────────────────────────────

function StaffDetailsPanel({ staff, onClose, setSelectedDeactivateStaff, setSelectedReactivateStaff, outletOptions, onUpdateStaff, editable }) {
  const role = ROLES.find((r) => r.id === staff.role)
  const [name, setName] = useState(staff.name)
  const [phone, setPhone] = useState(staff.phone)
  const [phoneCountryCode, setPhoneCountryCode] = useState(staff.countryCode || '60')
  const [email, setEmail] = useState(staff?.email)
  const [outlet, setOutlet] = useState(staff.outlet_ids)
  const [allOutlets, setAllOutlets] = useState(false)
  const [grantAllOutlets, setGrantAllOutlets] = useState(staff?.access_all_outlets == 1);
  const [selectedOutlets, setSelectedOutlets] = useState(staff?.outlets ? staff.outlets?.map((o) => o.id) : []);

  const handleOutletToggle = (id) => {
    setSelectedOutlets((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleSaveStaff = () => {
    let payload = {
      name: name,
      email: email,
      role_id: staff.role_id,
      phone: `${phoneCountryCode}${phone}`,
      access_all_outlets: grantAllOutlets,
      outlet_ids: grantAllOutlets ? outletOptions.map((o) => o.id) : selectedOutlets,
    }
    
    onUpdateStaff(staff.id, payload)
  }
  
  return (
    <>
      <div className="sr-panel-overlay" onClick={onClose} />
      <aside className="sr-panel">

        {/* Top bar */}
        <div className="sr-panel__header">
          <h3>Staff details</h3>
          <button onClick={onClose}><Icon icon="ph:x" width={18} /></button>
        </div>

        {/* Hero */}
        <div className="sr-panel__hero">
          {
            staff?.profile_photo_url ?
              <img src={staff.profile_photo_url} alt={staff.name} className="sr-panel__avatar" />
              : <Avatar className="sr-table__mui-avatar" />
          }

          <div className="sr-panel__hero-info">
            <p className="sr-panel__hero-name">{staff.name}</p>
            <p className="sr-panel__hero-meta">
              {`+${staff.countryCode || '60'}${staff.phone || ''}`}
              <span className="sr-panel__sep">·</span>
              Joined {staff.created_at ? moment(staff.created_at).format('MMM YYYY') : 'N/A'}
            </p>
          </div>
          <div className="sr-panel__hero-badges">
            <RoleBadge roleId={staff.role_name} />
            <span className={`sr-status-badge sr-status-badge--${staff.status == 1 ? 'active' : 'inactive'}`}>
              <Icon icon="ph:circle-fill" height={5} /> {staff.status == 1 ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Form body */}
        <div className="sr-panel__body">
          <p className="sr-panel__section-label">BASIC INFO</p>

          {/* Name */}
          <div className="sr-panel__field">
            <label>Name</label>
            <input
              className="sr-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!editable}
            />
          </div>

          {/* Role — read only */}
          <div className="sr-panel__field">
            <label>Role</label>
            <input
              className="sr-input sr-input--readonly"
              type="text"
              value={staff?.role_display_name}
              readOnly
            />
            {/* <p className="sr-panel__field-hint">{role?.description}</p> */}
          </div>

          {/* Phone */}
          <div className="sr-panel__field">
            <label>Phone number</label>
            {/* <div className="sr-phone-input">
              <span className="sr-phone-input__prefix">+60</span>
              <input
                type="text"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!editable}
              />
            </div> */}
            <PhoneInputField
              value={phone}
              onChange={setPhone}
              countryCode={phoneCountryCode}
              onCountryCodeChange={setPhoneCountryCode}
              disabled={!editable}
            />
          </div>

          {/* Email */}
          <div className="sr-panel__field">
            <label>
              Email address
              <span className="sr-panel__optional">(optional)</span>
            </label>
            <input
              className="sr-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!editable}
            />
          </div>

          {/* Outlet access */}
          <div className="staff-drawer__section">
            <label className="staff-drawer__label">
              Outlet access
            </label>

            <div className="staff-drawer__outlet-list">
              <div className='switch-container'>
                <label className="switch">
                  <input
                    checked={Boolean(grantAllOutlets)}
                    onChange={(event) => setGrantAllOutlets(!grantAllOutlets)}
                    type="checkbox"
                    disabled={!editable}
                  />

                  <span className="slider" />
                </label>
                <strong >Access to all outlets</strong>
              </div>

              {!grantAllOutlets && (
                outletOptions?.map((outlet) => (
                  <div className='switch-container' key={outlet?.id}>
                    <label className="switch">
                      <input
                        checked={selectedOutlets.includes(outlet.id)}
                        onChange={(event) => handleOutletToggle(outlet.id)}
                        type="checkbox"
                        disabled={!editable}
                      />

                      <span className="slider" />
                    </label>
                    <span >{outlet?.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer — two equal buttons */}
        {
          editable &&
          <div className="sr-panel__footer">
            {Number(staff.status) === 1 ? (
              <button className="sr-btn sr-btn--deactivate" onClick={() => setSelectedDeactivateStaff(staff)}>Deactivate account</button>
            ) : (
              <button className="sr-btn sr-btn--outline" onClick={() => setSelectedReactivateStaff(staff)}>Reactivate account</button>
            )}
            <button className="sr-btn sr-btn--save" onClick={() => handleSaveStaff()}>Save changes</button>
          </div>
        }
      </aside>
    </>
  )
}

// ─── Roles & Permissions Tab ──────────────────────────────────────────────────

function buildInitialPerms({ roleList }) {
  const state = {}
  roleList.forEach((role) => {
    const granted = new Set()
    // Initialize from role.permissions (could be array or object)
    if (role.permissions) {
      if (Array.isArray(role.permissions)) {
        role.permissions.forEach((perm) => granted.add(perm))
      } else if (typeof role.permissions === 'object') {
        // If role.permissions is an object (module -> array of permissions)
        Object.values(role.permissions).forEach((modulePerms) => {
          if (Array.isArray(modulePerms)) {
            modulePerms.forEach((perm) => {
              if (perm.enabled) {
                granted.add(perm.key)
              }
            })
          }
        })
      }
    } else if (role.access_set) {
      role.access_set.forEach((module) => {
        module.permissions.forEach((perm) => {
          if (perm.enabled) {
            granted.add(perm.name)
          }
        })
      })
    }
    state[role.id] = granted
  })
  return state
}

// Helper function to get grouped permissions from a role's access_set or permissions object
const getGroupedPermissionsForRole = (role) => {
  if (role.permissions && typeof role.permissions === 'object' && !Array.isArray(role.permissions)) {
    // Use role.permissions object structure
    return Object.entries(role.permissions).reduce((groups, [moduleName, modulePerms]) => {
      if (Array.isArray(modulePerms)) {
        groups[moduleName] = modulePerms.map(p => ({
          key: p.key,
          label: p.label
        }))
      }
      return groups
    }, {})
  }
  if (!role.access_set) return {}
  return role.access_set.reduce((groups, module) => {
    groups[module.label] = module.permissions.map(p => ({
      key: p.name,
      label: p.label
    }))
    return groups
  }, {})
}

function RolesTab({ setShowAddRole, roleList, setRoleList, onRoleSaved }) {
  const [allPerms, setAllPerms] = useState(buildInitialPerms({ roleList }))
  // All collapsed by default
  const [expanded, setExpanded] = useState({})
  const [loadingRoles, setLoadingRoles] = useState(new Set())
  const [savingRoles, setSavingRoles] = useState(new Set())
  const [saveError, setSaveError] = useState('')

  // Update allPerms when roleList changes
  useEffect(() => {
    setAllPerms(buildInitialPerms({ roleList }))
  }, [roleList])

  const fetchRoleDetails = async (roleId) => {
    if (loadingRoles.has(roleId)) return
    setLoadingRoles((prev) => new Set([...prev, roleId]))
    try {
      const response = await merchantApi.roles.show(roleId)
      const roleDetails = response?.data
      if (roleDetails) {
        setRoleList((prev) =>
          prev.map((role) =>
            role.id === roleId ? { ...role, ...roleDetails } : role
          )
        )
      }
    } catch (err) {
      console.error("Error fetching role details:", err)
    } finally {
      setLoadingRoles((prev) => {
        const next = new Set(prev)
        next.delete(roleId)
        return next
      })
    }
  }

  const toggleExpand = (roleId) => {
    const isExpanding = !expanded[roleId]
    setExpanded((prev) => ({ ...prev, [roleId]: isExpanding }))
    if (isExpanding) {
      fetchRoleDetails(roleId)
    }
  }

  const togglePerm = (roleId, key) => {
    setAllPerms((prev) => {
      const next = new Set(prev[roleId])
      next.has(key) ? next.delete(key) : next.add(key)
      return { ...prev, [roleId]: next }
    })
  }

  const toggleAllPerms = (roleId, groupedPermissions) => {
    setAllPerms((prev) => {
      const next = new Set(prev[roleId])
      // Get all permission keys from groupedPermissions
      const allKeys = Object.values(groupedPermissions).flatMap(module =>
        module.map(permission => permission.key)
      )
      const hasAll = allKeys.every((key) => next.has(key))
      if (hasAll) {
        // Remove all
        allKeys.forEach((key) => next.delete(key))
      } else {
        // Add all
        allKeys.forEach((key) => next.add(key))
      }
      return { ...prev, [roleId]: next }
    })
  }

  const handleRoleInfoChange = (roleId, field, value) => {
    setRoleList((prev) => {
      const next = prev.map((role) => {
        if (role.id === roleId) {
          return { ...role, [field]: value }
        }
        return role
      })
      return next
    })
  }

  const buildPermissionIdMap = (role) => {
    const map = new Map()

    if (role.permissions && typeof role.permissions === 'object' && !Array.isArray(role.permissions)) {
      Object.values(role.permissions).forEach((modulePerms) => {
        if (Array.isArray(modulePerms)) {
          modulePerms.forEach((perm) => {
            const id = Number(perm?.id)
            if (perm?.key && Number.isFinite(id)) {
              map.set(perm.key, id)
            }
          })
        }
      })
    }

    if (Array.isArray(role.permission_keys) && Array.isArray(role.permission_ids)) {
      role.permission_keys.forEach((key, index) => {
        const id = Number(role.permission_ids[index])
        if (key && Number.isFinite(id)) {
          map.set(key, id)
        }
      })
    }

    if (map.size === 0) {
      roleList.forEach((candidateRole) => {
        if (candidateRole.permissions && typeof candidateRole.permissions === 'object' && !Array.isArray(candidateRole.permissions)) {
          Object.values(candidateRole.permissions).forEach((modulePerms) => {
            if (Array.isArray(modulePerms)) {
              modulePerms.forEach((perm) => {
                const id = Number(perm?.id)
                if (perm?.key && Number.isFinite(id)) {
                  map.set(perm.key, id)
                }
              })
            }
          })
        }
      })
    }

    return map
  }

  const handleSaveRole = async (role, event) => {
    event?.preventDefault()
    event?.stopPropagation()

    const selectedKeys = Array.from(allPerms[role.id] || [])
    const permissionIdMap = buildPermissionIdMap(role)
    const permissionIds = Array.from(new Set(
      selectedKeys
        .map((key) => permissionIdMap.get(key))
        .filter((id) => Number.isFinite(id))
    ))

    if (selectedKeys.length > 0 && permissionIds.length === 0) {
      setSaveError('Unable to resolve permission IDs for this role. Please reload role details and try again.')
      return
    }

    const payload = {
      display_name: role.display_name,
      icon: role.icon,
      description: role.description,
      permission_ids: permissionIds,
    }

    setSaveError('')
    setSavingRoles((prev) => new Set([...prev, role.id]))
    try {
      await merchantApi.roles.update(role.id, payload)

      // setRoleList((prev) => prev.map((item) => {
      //   if (item.id !== role.id) return item
      //   return {
      //     ...item,
      //     display_name: payload.display_name,
      //     icon: payload.icon,
      //     description: payload.description,
      //     permission_ids: payload.permission_ids,
      //     permission_keys: selectedKeys,
      //   }
      // }))

      onRoleSaved?.(role, payload)
    } catch (err) {
      setSaveError(err?.message || 'Unable to update role.')
    } finally {
      setExpanded((prev) => ({ ...prev, [role.id]: false }))
      setSavingRoles((prev) => {
        const next = new Set(prev)
        next.delete(role.id)
        return next
      })
    }
  }

  return (
    <div className="sr-roles-list">
      <div className="sr-roles-header">
        <h1 className="sr-page__title">Roles & Permissions</h1>
        <button className="sr-btn sr-btn--primary" onClick={() => setShowAddRole(true)}>
          Add Role
        </button>
      </div>
      {saveError && <div className="sr-error">{saveError}</div>}
      {roleList?.length > 0 ? roleList.map((role) => {
        const granted = allPerms[role.id]
        const isOpen = !!expanded[role.id]
        const groupedPermissions = getGroupedPermissionsForRole(role)
        const allKeys = Object.values(groupedPermissions).flatMap(module =>
          module.map(permission => permission.key)
        )
        
        const hasAll = allKeys.every((key) => granted?.has(key))
        return (
          <div key={role.id} className={`sr-role-card ${isOpen ? 'sr-role-card--open' : ''}`}>

            {/* Clickable header — chevron right = collapsed, down = expanded */}
            <button
              className="sr-role-card__header"
              onClick={() => toggleExpand(role.id)}
              aria-expanded={isOpen}
              type="button"
            >
              <div className="sr-role-card__title-row">
                <span className={`sr-role-card__icon`}>
                  <Icon icon={role.icon} size={16} />
                </span>
                <div>
                  <p className="sr-role-card__name">{role.display_name}</p>
                  <p className="sr-role-card__desc">{role.description}</p>
                </div>
              </div>

              <div className="sr-role-card__header-right">
                {isOpen ? (
                  <button
                    className="sr-btn sr-btn--primary"
                    type="button"
                    onClick={(event) => handleSaveRole(role, event)}
                    disabled={loadingRoles.has(role.id) || savingRoles.has(role.id)}
                  >
                    {savingRoles.has(role.id) ? 'Saving...' : 'Save'}
                  </button>
                ) : 'Edit'}
                <span className="sr-role-card__edit-label">
                  {isOpen ? <Icon icon={'ph:caret-up'} size={16} /> : <Icon icon={'ph:caret-right'} size={16} />}
                </span>
              </div>
            </button>

            {/* Collapsible permissions body */}
            {isOpen && (
              <Grid container spacing={2} p={4}>
                {loadingRoles.has(role.id) ? (
                  <Grid item sm={12} xs={12}>
                    <div className="sr-loading">Loading role permissions...</div>
                  </Grid>
                ) : (
                  <>
                    <Grid item sm={12}>
                      <p className="sr-perm-section__label">Role</p>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <div className="sr-modal__field">
                        <label>Role</label>
                        <input className="sr-input" type="text" placeholder="Role name" value={role.display_name} onChange={(e) => handleRoleInfoChange(role.id, 'display_name', e.target.value)} />
                      </div>
                    </Grid>
                    <Grid item sm={6} xs={12}>
                      <IconSelect
                        label="Icon"
                        value={role.icon}
                        options={ICON_OPTIONS}
                        onChange={(e) => handleRoleInfoChange(role.id, 'icon', e.target.value)}
                        placeholder="Select icon"
                      />
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <div className="sr-modal__field">
                        <label>Description</label>
                        <input className="sr-input" type="text" placeholder="Role description" value={role.description} onChange={(e) => handleRoleInfoChange(role.id, 'description', e.target.value)} />
                      </div>
                    </Grid>
                    <Grid item sm={12} xs={12} my={2}><Divider /></Grid>
                    <Grid item sm={6} xs={12}>
                      <p className="sr-perm-section__label">Permission Settings</p>
                    </Grid>
                    <Grid item sm={12} xs={12}>
                      <label key={'select-all' + role.name} className="sr-perm-row">
                        <span
                          className={`sr-perm-toggle ${hasAll ? 'sr-perm-toggle--on' : ''}`}
                          onClick={(e) => { e.preventDefault(); toggleAllPerms(role.id, groupedPermissions) }}
                          role="switch"
                          aria-checked={hasAll}
                          tabIndex={0}
                          onKeyDown={(e) => e.key === ' ' && toggleAllPerms(role.id, groupedPermissions)}
                        >
                          <span className="sr-perm-toggle__thumb" />
                        </span>
                        <span className="sr-perm-row__name">Select all</span>
                      </label>
                    </Grid>
                    {groupedPermissions && Object.entries(groupedPermissions).map(([moduleName, sections]) => (
                      <Grid item sm={6} xs={12}>
                        <div key={moduleName} className="sr-perm-section">
                          <p className="sr-perm-section__label">{moduleName}<Divider /></p>
                          {sections.map((section, idx) => {
                            const key = section.key
                            const on = granted.has(key)
                            return (
                              <label key={key} className="sr-perm-row">
                                <span
                                  className={`sr-perm-toggle ${on ? 'sr-perm-toggle--on' : ''}`}
                                  onClick={(e) => { e.preventDefault(); togglePerm(role.id, key) }}
                                  role="switch"
                                  aria-checked={on}
                                  tabIndex={0}
                                  onKeyDown={(e) => e.key === ' ' && togglePerm(role.id, key)}
                                >
                                  <span className="sr-perm-toggle__thumb" />
                                </span>
                                <span className="sr-perm-row__name">{section.label}</span>
                              </label>
                            )
                          })}
                        </div>
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            )}

          </div>
        )
      }) : <div>No roles available</div>}
    </div>
  )
}

// ─── Staff Members Tab ────────────────────────────────────────────────────────

function StaffTab({ staffRows = null, loading = false, error = '', search, onSelect, onPageChange, onSearchChange, filter, setFilter, outletOptions, roleOptions, setFilterOpen }) {

  const columns = [
    {
      field: 'name',
      header: 'Name',
      render: (row) => (
        <div className="sr-table__member">
          {
            row.profile_photo_url ?
              <img src={row.profile_photo_url} alt={row.name} className="sr-table__avatar" />
              : <Avatar className="sr-table__mui-avatar" />
          }

          <span>{row.name}</span>
        </div>
      ),
    },
    {
      field: 'email',
      header: 'Email',
    },
    {
      field: 'phone',
      header: 'Phone',
      render: (row) => `+${row.countryCode}${row.phone}`,
    },
    {
      field: 'role_display_name',
      header: 'Role',
      render: (row) => <RoleBadge roleId={row.role_name} />,
    },
    {
      field: 'outlet',
      header: 'Outlet',
      minWidth: 200,
      render: (row) => {
        return row.outlets?.length > 0 ? row.outlets?.map((outlet) => outlet.name).join(', ') : '-'
      }
    },
    {
      field: 'last_logged_in',
      header: 'Last Login',
    },
    {
      field: 'status',
      header: 'Status',
      render: (row) => (
        <div className={`sr-status-badge sr-status-badge--${row.status == 1 ? 'active' : 'inactive'}`}>
          <Icon icon="ph:circle-fill" height={5} /> {row.status == 1 ? 'Active' : 'Inactive'}
        </div>
      ),
    },
    {
      field: 'actions',
      header: '',
      render: (row) => (<Icon icon={'ph:caret-right'} height={16} />)
    }
  ]

  return (
    <>
      {/* Filters */}
      <div className="sr-toolbar">
        <div className="sr-toolbar__filters">
          <FilterDropdown
            label="Outlet"
            value={filter?.outlet}
            options={outletOptions}
            onChange={(value) => setFilter({ ...filter, outlet: value })}
            valueKey={'id'}
            labelKey={'name'}
          />
          <FilterDropdown
            label="Status"
            value={filter?.status}
            onChange={(value) => setFilter({ ...filter, status: value })}
            options={[{ value: 1, label: 'Active' }, { value: 0, label: 'Inactive' }]}
          />
          <FilterDropdown
            label="Role"
            value={filter?.role}
            options={roleOptions}
            onChange={(value) => setFilter({ ...filter, role: value })}
            valueKey={'id'}
            labelKey={'name'}
          />
          <button className="customers-page__filter-btn" onClick={() => setFilterOpen(true)}>
            <Icon icon={'ph:funnel-simple'} className='customers-page__filter-icon' />Filters
          </button>
        </div>
        <div className="sr-search">
          <Icon icon="ph:magnifying-glass" width={16} />
          <input type="text" placeholder="Search users" value={search} onChange={(e) => onSearchChange(e)} />
        </div>
      </div>

      {error && <div className="sr-error">{error}</div>}
      {loading && <div className="sr-loading">Loading staff...</div>}

      <AppTable
        columns={columns}
        rows={staffRows?.data || []}
        pagination={staffRows}
        loading={loading}
        onRowClick={(row) => { onSelect(row) }}
        onPageChange={(page) => {
          onPageChange(page)
        }}
      />
    </>
  )
}

function StaffFilter({ open, setOpen, setValues, values, applyFilters, roleOptions, outletOptions }) {
  const filters = [
    {
      key: 'outlet',
      label: 'Outlet',
      type: 'checked-list',
      options: outletOptions.map((outlet) => ({ value: outlet.id, label: outlet.name })),
      placeholder: 'Select outlet',
    },
    {
      key: 'role',
      label: 'Role',
      type: 'chip',
      options: roleOptions.map((role) => ({ value: role.id, label: role.display_name })),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'chip',
      options: [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Pending invite' },
        { value: 0, label: 'Inactive' },
      ],
    },
  ]

  return (
    <FilterSideModal
      open={open}
      onClose={() => setOpen(false)}
      onApply={(vals) => {
        setOpen(false)
        applyFilters(vals)
      }}
      onReset={() => setValues({})}
      values={values}
      onChange={(key, val) => setValues((p) => ({ ...p, [key]: val }))}
      filters={filters}
    />
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function StaffRolesPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('staff')
  const [showInvite, setShowInvite] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [selectedDeactivateStaff, setSelectedDeactivateStaff] = useState(null)
  const [selectedReactivateStaff, setSelectedReactivateStaff] = useState(null)
  const [outletOptions, setOutletOptions] = useState([])
  const [notice, setNotice] = useState('')
  const [pageError, setPageError] = useState('')
  const [loadingOutlets, setLoadingOutlets] = useState(false)
  const [userInfo, setUserInfo] = useState(null)
  const [showAddRole, setShowAddRole] = useState(false)
  const [staffList, setStaffList] = useState([])
  const [roleList, setRoleList] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [permissionDetails, setPermissionDetails] = useState([])
  const [filter, setFilter] = useState({ outlet: '', status: '', role: '' })
  const [filterOpen, setFilterOpen] = useState(false)
  const [selectedOutletId, setSelectedOutletId] = useState(null)
  // permission states
  const [editable, setEditable] = useState(true)
  const [exportable, setExportable] = useState(true)
  const [creatable, setCreatable] = useState(true)

  useEffect(() => {
    let active = true

    const loadOutlets = async () => {
      setLoadingOutlets(true)
      setPageError('')

      try {
        const response = await merchantApi.outlets.list({ page: 0 })
        const normalized = response?.data

        if (active && normalized.length) {
          setOutletOptions(normalized)
        }
      } catch (err) {
        if (active) {
          setPageError(err?.message || 'Unable to load outlets. Using local outlet options.')
        }
      } finally {
        if (active) setLoadingOutlets(false)
      }
    }

    loadUserData()
    loadRoleData()
    loadOutlets()

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if(selectedOutletId){
     loadStaffData() 
    }
  }, [currentPage, search, filter, selectedOutletId])

  const loadUserData = async () => {
    try {
      const response = await merchantApi.auth.me();
      if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
        navigate('/unauthorized')
        return
      }

      const generalData = await merchantApi.general.systemInfo()
      const permissionData = await merchantApi.general.permissions()
      const data = response.data
      setSelectedOutletId(data?.default_outlet?.id || null)
      setUserInfo(data || null)
      setPermissionDetails(permissionData.data || [])
      setRoleOptions(generalData.data?.roles || [])
      // setEditable(data?.permissions?.includes(`edit-${moduleCode}`))
      setExportable(data?.permissions?.includes(`export-${moduleCode}`))
      setCreatable(data?.permissions?.includes(`create-${moduleCode}`))
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    }
  }

  const loadStaffData = async () => {
    try {
      let payload = {
        page: currentPage,
        search,
        "filter[outlet_id]": filter?.outlet ? filter.outlet : undefined,
        "filter[role_id]": filter?.role ? filter.role : undefined,
        "filter[status][]": filter?.status,
      }
      const response = await merchantApi.staff.list(payload)
      const normalizedRows = Array.isArray(response?.data?.data)
        ? response.data.data.map(normalizeStaffPhoneFields)
        : []

      setStaffList({
        ...(response?.data || {}),
        data: normalizedRows,
      })
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    }
  }

  const loadRoleData = async () => {
    try {
      const response = await merchantApi.roles.list()
      setRoleList(response?.data || [])
    } catch (error) {
      setPageError(error.message || 'Error fetching data.')
    }
  }

  const groupedPermissions = useMemo(() => {
    return permissionDetails.reduce((groups, permission) => {
      const [, module] = permission.key.split('-')

      if (!groups[module]) {
        groups[module] = []
      }

      groups[module].push(permission)

      return groups
    }, {})
  }, [permissionDetails])

  const staffRows = useMemo(() => staffList, [staffList])
  
  const handleInvited = (_response, payload) => {
    setNotice(`Invitation sent to ${payload.name}.`)
  }

  const handleRoleAdded = (_response, payload) => {
    setNotice(`Role added: ${payload.name}.`)
  }

  const handleDeactivated = (_response, payload) => {
    setSelectedStaff(null)
    loadStaffData() // Refresh the staff list after deactivation
    setNotice(`Account deactivated for ${payload.name}.`)
  }

  const handleReactivated = (_response, payload) => {
    loadStaffData()
    setSelectedStaff(null)
    setNotice(`Account reactivated for ${payload.name}.`)
  }

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setCurrentPage(1) // Reset to first page on new search
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleUpdateStaff = async (staffId, payload) => {
    try {
      const response = await merchantApi.staff.update(staffId, payload)
      setNotice(`Staff member updated: ${payload.name}.`)
      setPageError('') // Clear any previous errors
      setSelectedStaff(null) // Close the details panel
      loadStaffData() // Refresh the staff list after update
    } catch (error) {
      setPageError(error.message || 'Error updating staff member.')
    }
  }

  const handleExport = async () => {
    try {
      const response = await merchantApi.staff.export()
      const byteCharacters = atob(response?.data?.base64);
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: response?.data?.mime_type || 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = response?.data?.file_name || 'staff.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (error) {
      setPageError(error.message || 'Error exporting staff data.')
    }
  }

  return (
    <div className="sr-page">
      {/* Header */}
      <Header
        items={[
          { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
          { label: <>Staff &amp; roles</>, current: true },
        ]}
      />
      <div className="sr-page__header">
        <div>
          <h1 className="sr-page__title">Staff</h1>
          <p className="sr-page__subtitle">Manage access and permissions</p>
        </div>
        <div className="sr-page__actions">
          {
            activeTab === 'staff' && exportable &&
            <button className="sr-btn sr-btn--outline" onClick={() => handleExport()}>
              <Icon icon="ph:arrow-line-down" width={16} /> Export CSV
            </button>
          }
          {
            activeTab === 'staff' && creatable && (
              <button className="sr-btn sr-btn--primary" onClick={() => setShowInvite(true)}>
                <Icon icon="ph:plus" width={16} /> Invite staff
              </button>
            )
          }
        </div>
      </div>

      {notice && <div className="sr-success">{notice}</div>}
      {pageError && <div className="sr-error">{pageError}</div>}

      {/* Tabs */}
      <div className="sr-tabs">
        <button
          className={`sr-tab ${activeTab === 'staff' ? 'sr-tab--active' : ''}`}
          onClick={() => setActiveTab('staff')}
        >
          Staff members
        </button>
        <button
          className={`sr-tab ${activeTab === 'roles' ? 'sr-tab--active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          Roles &amp; permissions
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'staff' && (
        <StaffTab
          staffRows={staffRows}
          loading={loadingOutlets}
          search={search}
          onSelect={setSelectedStaff}
          onSearchChange={handleSearchChange}
          setFilterOpen={setFilterOpen}
          onPageChange={handlePageChange}
          filter={filter}
          setFilter={setFilter}
          outletOptions={outletOptions}
          roleOptions={roleOptions}
        />
      )}
      {
        activeTab === 'roles' &&
        <RolesTab
          setShowAddRole={setShowAddRole}
          roleList={roleList}
          setRoleList={setRoleList}
          onRoleSaved={(role, payload) => {
            setNotice(`Role updated: ${payload.display_name || role.display_name}.`)
            loadRoleData()
          }}
        />
      }

      {/* Panels & Modals */}
      {showInvite && (
        <InviteModal
          outlets={outletOptions}
          roles={roleOptions}
          onClose={() => setShowInvite(false)}
          onInvited={handleInvited}
        />
      )}
      {showAddRole && (
        <CreateRoleModal
          outlets={outletOptions}
          onClose={() => setShowAddRole(false)}
          onRoleAdded={handleRoleAdded}
        />
      )}
      {selectedDeactivateStaff && (
        <DeactivateModal
          selectedStaff={selectedDeactivateStaff}
          onClose={() => setSelectedDeactivateStaff(null)}
          onDeactivated={handleDeactivated}
        />
      )}
      {selectedReactivateStaff && (
        <ReactivateModal
          selectedStaff={selectedReactivateStaff}
          onClose={() => setSelectedReactivateStaff(null)}
          onReactivated={handleReactivated}
        />
      )}
      {selectedStaff && (
        <StaffDetailsPanel
          staff={selectedStaff}
          onClose={() => setSelectedStaff(null)}
          setSelectedDeactivateStaff={setSelectedDeactivateStaff}
          setSelectedReactivateStaff={setSelectedReactivateStaff}
          outletOptions={outletOptions}
          onUpdateStaff={handleUpdateStaff}
          editable={editable}
        />
      )}
      <StaffFilter
        open={filterOpen}
        setOpen={setFilterOpen}
        values={filter}
        setValues={setFilter}
        applyFilters={loadStaffData}
        roleOptions={roleOptions}
        outletOptions={outletOptions}
      />
    </div>
  )
}
